import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Clock, ChevronRight, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { blogPosts as legacyBlogPosts } from '../blogData';
import { additionalBlogPosts } from '../newContent';
import { companyInfo } from '../mock';
import SEO from './SEO';
import { blogListingSchema, breadcrumbSchema } from '../seo/schemas';

// Newest posts first
const blogPosts = [...additionalBlogPosts, ...legacyBlogPosts];

const BlogListing = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(blogPosts.map(post => post.category))];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Brooklyn Electrician Tips, Guides & Articles"
        description="Expert electrical advice from experienced Brooklyn electricians. Read in-depth guides on EV chargers, panel upgrades, smart homes, emergency repairs, and more for Brooklyn, Queens, and Manhattan homeowners."
        path="/blog"
        keywords="electrician blog brooklyn, electrical tips, EV charger guide, panel upgrade guide, electrical safety"
        jsonLd={[
          blogListingSchema(blogPosts),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
          ]),
        ]}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2" data-testid="blog-home-link">
              <img
                src="https://customer-assets.emergentagent.com/job_jetco-electrical/artifacts/x1j92naa_Untitled%20design.png"
                alt="Jetco Electrical Contractors logo"
                className="h-12 w-auto"
              />
            </Link>
            <a href={`tel:${companyInfo.phone}`} data-testid="blog-call-now-link">
              <Button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold shadow-sm">
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-b from-gray-50 to-white relative">
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              Electrical <span className="text-yellow-500">Tips & Guides</span>
            </h1>
            <div className="w-24 h-1 bg-yellow-400 mx-auto mb-4"></div>
            <p className="text-lg text-gray-600">
              Expert advice on electrical systems, safety, and home improvements from Brooklyn's trusted electricians.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-yellow-400"
                  data-testid="blog-search-input"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {categories.map(category => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? "default" : "outline"}
                    onClick={() => setSelectedCategory(category)}
                    className={selectedCategory === category
                      ? "bg-yellow-400 text-black font-bold hover:bg-yellow-500"
                      : "border-gray-300 text-gray-700 hover:border-yellow-400 hover:text-yellow-600 bg-white"}
                    data-testid={`blog-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {filteredPosts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-xl text-gray-500">No articles found matching your search.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map(post => (
                  <Card key={post.id} className="hover:shadow-lg transition-all border border-gray-200 hover:border-yellow-400 flex flex-col bg-white" data-testid={`blog-card-${post.slug}`}>
                    <div className="aspect-video overflow-hidden rounded-t-lg">
                      <img
                        src={post.image}
                        alt={post.title}
                        loading="lazy"
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <CardHeader className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-yellow-700 bg-yellow-100 border border-yellow-300 px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                        <span className="text-sm text-gray-500 flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {post.readTime}
                        </span>
                      </div>
                      <CardTitle className="text-xl mb-2 text-gray-900">{post.title}</CardTitle>
                      <CardDescription className="text-gray-600">{post.excerpt}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">{post.date}</span>
                        <Link to={`/blog/${post.slug}`} data-testid={`blog-read-${post.slug}`}>
                          <Button variant="ghost" className="text-yellow-700 hover:text-yellow-900 hover:bg-yellow-50">
                            Read More
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-yellow-400">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
            Need Professional Electrical Help?
          </h2>
          <p className="text-lg text-gray-900 mb-8">
            Our expert electricians are ready to help with your project
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/?scroll=contact" data-testid="blog-cta-estimate-link">
              <Button size="lg" className="bg-black text-white hover:bg-gray-900">
                Get Free Estimate
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`} data-testid="blog-cta-phone-link">
              <Button size="lg" variant="outline" className="border-2 border-black text-black hover:bg-black hover:text-white bg-transparent">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-300">© 2025 {companyInfo.name}. All rights reserved.</p>
          <div className="mt-4">
            <a href={`tel:${companyInfo.phone}`} className="text-yellow-400 hover:text-yellow-300 transition-colors">{companyInfo.phone}</a>
            <span className="mx-3 text-gray-600">|</span>
            <a href={`mailto:${companyInfo.email}`} className="text-yellow-400 hover:text-yellow-300 transition-colors">{companyInfo.email}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogListing;
